
import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import Select from '../../components/base/Select';
import Modal from '../../components/base/Modal';

interface Reference {
  id: string;
  category: string;
  name: string;
  price: number;
  unit?: string;
  supplier?: string;
  notes?: string;
  file_name?: string;
  file_url?: string;
  file_size?: number;
  lastUpdated: string;
}

interface ReferenceTable {
  id: string;
  name: string;
  items: Array<{
    product: string;
    price: number;
  }>;
  totalPrice: number;
  category: string;
  notes?: string;
  lastUpdated: string;
}

const categories = [
  'Automaten Preise',
  'Zoll Kosten',
  'Ersatzteile',
  'Wartung',
  'Transport',
  'Standort Kosten',
  'Lieferanten',
  'Nayax',
  'Sonstiges'
];

const units = [
  'CHF',
  'CHF/Stück',
  'CHF/kg',
  'CHF/m²',
  'CHF/Monat',
  'CHF/Jahr',
  '%',
  'Stück'
];

export default function Referenzen() {
  const { user } = useAuth();
  const [references, setReferences] = useState<Reference[]>([]);
  const [referenceTables, setReferenceTables] = useState<ReferenceTable[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTableModalOpen, setIsTableModalOpen] = useState(false);
  const [isTableViewModalOpen, setIsTableViewModalOpen] = useState(false);
  const [editingReference, setEditingReference] = useState<Reference | null>(null);
  const [editingTable, setEditingTable] = useState<ReferenceTable | null>(null);
  const [viewingTable, setViewingTable] = useState<ReferenceTable | null>(null);
  const [uploading, setUploading] = useState(false);

  const [form, setForm] = useState({
    category: '',
    name: '',
    price: '',
    unit: 'CHF',
    supplier: '',
    notes: '',
    file: null as File | null
  });

  const [tableForm, setTableForm] = useState({
    name: '',
    category: '',
    notes: '',
    items: [{ product: '', price: 0 }]
  });

  useEffect(() => {
    if (user) {
      loadReferences();
      loadReferenceTables();
    }
  }, [user]);

  const loadReferences = async () => {
    try {
      // KORRIGIERT: Verwende erlaubten data_type 'automat' mit machine_type Filter
      const { data, error } = await supabase
        .from('daten')
        .select('*')
        .eq('user_id', user?.id)
        .eq('data_type', 'automat') // GEÄNDERT: Verwende erlaubten data_type
        .order('created_at', { ascending: false });

      if (error) throw error;

      // KORRIGIERT: Filtere nur echte Referenzen
      const references = data
        .filter(item => {
          return item.data && item.data.machine_type === 'reference';
        })
        .map(item => ({
          id: item.id,
          ...item.data
        }));

      references.sort((a, b) => a.category.localeCompare(b.category));
      setReferences(references);
    } catch (error) {
      console.error('Error loading references:', error);
      setError('Fehler beim Laden der Referenzen');
    } finally {
      setLoading(false);
    }
  };

  const loadReferenceTables = async () => {
    try {
      // KORRIGIERT: Verwende erlaubten data_type 'automat' mit machine_type Filter
      const { data, error } = await supabase
        .from('daten')
        .select('*')
        .eq('user_id', user?.id)
        .eq('data_type', 'automat') // GEÄNDERT: Verwende erlaubten data_type
        .order('created_at', { ascending: false });

      if (error) throw error;

      // KORRIGIERT: Filtere nur echte Referenz-Tabellen
      const tables = data
        .filter(item => {
          return item.data && item.data.machine_type === 'reference_table';
        })
        .map(item => ({
          id: item.id,
          ...item.data
        }));

      tables.sort((a, b) => a.category.localeCompare(b.category));
      setReferenceTables(tables);
    } catch (error) {
      console.error('Error loading reference tables:', error);
    }
  };

  // Upload file to Supabase Storage
  const uploadFile = async (file: File): Promise<{ url: string; error?: any }> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `${user?.id}/${fileName}`;

      const { data, error } = await supabase.storage
        .from('references')
        .upload(filePath, file);

      if (error) {
        return { url: '', error };
      }

      const { data: { publicUrl } } = supabase.storage
        .from('references')
        .getPublicUrl(filePath);

      return { url: publicUrl };
    } catch (error) {
      return { url: '', error };
    }
  };

  // Download/View file
  const handleFileView = (fileUrl: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // File change handler
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setForm(prev => ({ ...prev, file }));
  };

  const validateForm = () => {
    if (!form.category.trim()) {
      setError('Bitte wählen Sie eine Kategorie aus');
      return false;
    }
    if (!form.name.trim()) {
      setError('Bitte geben Sie einen Namen ein');
      return false;
    }
    if (!form.price || isNaN(parseFloat(form.price)) || parseFloat(form.price) < 0) {
      setError('Bitte geben Sie einen gültigen Preis ein');
      return false;
    }
    if (!form.unit.trim()) {
      setError('Bitte wählen Sie eine Einheit aus');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    setUploading(true);

    if (!validateForm()) {
      setSaving(false);
      setUploading(false);
      return;
    }

    if (!user?.id) {
      setError('Benutzer nicht authentifiziert');
      setSaving(false);
      setUploading(false);
      return;
    }

    try {
      let fileUrl = undefined;
      let fileName = undefined;
      let fileSize = undefined;

      if (form.file) {
        const { url, error } = await uploadFile(form.file);
        if (error) {
          console.error('Error uploading file:', error);
          setError('Fehler beim Hochladen der Datei');
          setSaving(false);
          setUploading(false);
          return;
        }
        fileUrl = url;
        fileName = form.file.name;
        fileSize = form.file.size;
      }

      // KORRIGIERT: Verwende erlaubten data_type mit machine_type Unterscheidung
      const referenceData = {
        type: 'reference',
        machine_type: 'reference', // WICHTIG: Unterscheidung von echten Automaten
        category: form.category.trim(),
        name: form.name.trim(),
        price: parseFloat(form.price),
        unit: form.unit.trim(),
        supplier: form.supplier.trim() || undefined,
        notes: form.notes.trim() || undefined,
        file_name: fileName,
        file_url: fileUrl,
        file_size: fileSize,
        lastUpdated: new Date().toISOString()
      };

      if (editingReference) {
        const { error } = await supabase
          .from('daten')
          .update({
            data: referenceData,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingReference.id)
          .eq('user_id', user.id);

        if (error) throw error;

        setReferences(prev =>
          prev.map(ref =>
            ref.id === editingReference.id
              ? { id: editingReference.id, ...referenceData }
              : ref
          ).sort((a, b) => a.category.localeCompare(b.category))
        );
      } else {
        const { data, error } = await supabase
          .from('daten')
          .insert({
            user_id: user.id,
            data_type: 'automat', // KORRIGIERT: Verwende erlaubten data_type
            data: referenceData,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .select()
          .single();

        if (error) throw error;

        if (data) {
          setReferences(prev => [
            { id: data.id, ...referenceData },
            ...prev
          ].sort((a, b) => a.category.localeCompare(b.category)));
        }
      }

      resetForm();
      setIsModalOpen(false);
      setError('');
    } catch (error: any) {
      console.error('Error saving reference:', error);
      setError(error.message || 'Fehler beim Speichern der Referenz');
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  const handleEdit = (reference: Reference) => {
    setEditingReference(reference);
    setForm({
      category: reference.category,
      name: reference.name,
      price: reference.price.toString(),
      unit: reference.unit || 'CHF',
      supplier: reference.supplier || '',
      notes: reference.notes || '',
      file: null
    });
    setError('');
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Möchten Sie diese Referenz wirklich löschen?')) return;

    try {
      const { error } = await supabase
        .from('daten')
        .delete()
        .eq('id', id)
        .eq('user_id', user?.id);

      if (error) throw error;

      setReferences(prev => prev.filter(ref => ref.id !== id));
    } catch (error: any) {
      console.error('Error deleting reference:', error);
      setError(error.message || 'Fehler beim Löschen der Referenz');
    }
  };

  const resetForm = () => {
    setForm({
      category: '',
      name: '',
      price: '',
      unit: 'CHF',
      supplier: '',
      notes: '',
      file: null
    });
    setEditingReference(null);
    setError('');
  };

  const resetTableForm = () => {
    setTableForm({
      name: '',
      category: '',
      notes: '',
      items: [{ product: '', price: 0 }]
    });
    setEditingTable(null);
    setError('');
  };

  const addTableItem = () => {
    setTableForm(prev => ({
      ...prev,
      items: [...prev.items, { product: '', price: 0 }]
    }));
  };

  const removeTableItem = (index: number) => {
    if (tableForm.items.length > 1) {
      setTableForm(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
    }
  };

  const updateTableItem = (index: number, field: 'product' | 'price', value: string | number) => {
    setTableForm(prev => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const calculateTotalPrice = () => {
    return tableForm.items.reduce((sum, item) => sum + (item.price || 0), 0);
  };

  const validateTableForm = () => {
    if (!tableForm.name.trim()) {
      setError('Bitte geben Sie einen Namen für die Tabelle ein');
      return false;
    }
    if (!tableForm.category.trim()) {
      setError('Bitte wählen Sie eine Kategorie aus');
      return false;
    }
    if (tableForm.items.some(item => !item.product.trim())) {
      setError('Bitte füllen Sie alle Produktfelder aus');
      return false;
    }
    if (tableForm.items.some(item => isNaN(item.price) || item.price < 0)) {
      setError('Bitte geben Sie gültige Preise ein');
      return false;
    }
    return true;
  };

  const handleTableSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    if (!validateTableForm()) {
      setSaving(false);
      return;
    }

    if (!user?.id) {
      setError('Benutzer nicht authentifiziert');
      setSaving(false);
      return;
    }

    try {
      const totalPrice = calculateTotalPrice();
      // KORRIGIERT: Verwende erlaubten data_type mit machine_type Unterscheidung
      const tableData = {
        type: 'reference_table',
        machine_type: 'reference_table', // WICHTIG: Unterscheidung von echten Automaten
        name: tableForm.name.trim(),
        category: tableForm.category.trim(),
        items: tableForm.items.map(item => ({
          product: item.product.trim(),
          price: parseFloat(item.price.toString())
        })),
        totalPrice,
        notes: tableForm.notes.trim() || undefined,
        lastUpdated: new Date().toISOString()
      };

      if (editingTable) {
        const { error } = await supabase
          .from('daten')
          .update({
            data: tableData,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingTable.id)
          .eq('user_id', user.id);

        if (error) throw error;

        setReferenceTables(prev =>
          prev.map(table =>
            table.id === editingTable.id
              ? { id: editingTable.id, ...tableData }
              : table
          ).sort((a, b) => a.category.localeCompare(b.category))
        );
      } else {
        const { data, error } = await supabase
          .from('daten')
          .insert({
            user_id: user.id,
            data_type: 'automat', // KORRIGIERT: Verwende erlaubten data_type
            data: tableData,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .select()
          .single();

        if (error) throw error;

        if (data) {
          setReferenceTables(prev => [
            { id: data.id, ...tableData },
            ...prev
          ].sort((a, b) => a.category.localeCompare(b.category)));
        }
      }

      resetTableForm();
      setIsTableModalOpen(false);
      setError('');
    } catch (error: any) {
      console.error('Error saving reference table:', error);
      setError(error.message || 'Fehler beim Speichern der Referenz-Tabelle');
    } finally {
      setSaving(false);
    }
  };

  const handleTableEdit = (table: ReferenceTable) => {
    setEditingTable(table);
    setTableForm({
      name: table.name,
      category: table.category,
      notes: table.notes || '',
      items: [...table.items]
    });
    setError('');
    setIsTableModalOpen(true);
  };

  const handleTableDelete = async (id: string) => {
    if (!confirm('Möchten Sie diese Referenz-Tabelle wirklich löschen?')) return;

    try {
      const { error } = await supabase
        .from('daten')
        .delete()
        .eq('id', id)
        .eq('user_id', user?.id);

      if (error) throw error;

      setReferenceTables(prev => prev.filter(table => table.id !== id));
    } catch (error: any) {
      console.error('Error deleting reference table:', error);
      setError(error.message || 'Fehler beim Löschen der Referenz-Tabelle');
    }
  };

  const handleTableView = (table: ReferenceTable) => {
    setViewingTable(table);
    setIsTableViewModalOpen(true);
  };

  const filteredReferences = references.filter(ref => {
    const matchesSearch =
      ref.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ref.supplier?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ref.notes?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || ref.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const filteredTables = referenceTables.filter(table => {
    const matchesSearch =
      table.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      table.items.some(item => item.product.toLowerCase().includes(searchTerm.toLowerCase())) ||
      table.notes?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || table.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const allFilteredItems = [...filteredReferences, ...filteredTables];

  const groupedItems = allFilteredItems.reduce((groups, item) => {
    const category = item.category;
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(item);
    return groups;
  }, {} as Record<string, (Reference | ReferenceTable)[]>);

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-line text-4xl text-blue-600 animate-spin mb-4"></i>
          <p className="text-gray-600">Lade Referenzen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full overflow-x-hidden">
      <div className="w-full max-w-full overflow-x-hidden">
        <div className="bg-white rounded-lg shadow-sm border w-full overflow-x-hidden">
          <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 border-b border-gray-200">
            <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:justify-between sm:items-center">
              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 flex items-center">
                  <i className="ri-bookmark-line mr-2 sm:mr-3 text-blue-600 text-lg sm:text-xl"></i>
                  Referenzen
                  <span className="ml-2 sm:ml-3 text-xs sm:text-sm text-blue-600">
                    <i className="ri-cloud-line mr-1"></i>
                    Supabase
                  </span>
                </h1>
                <p className="text-gray-600 mt-1 text-xs sm:text-base break-words">
                  Preise & Kosten-Referenzen verwalten
                </p>
              </div>
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
                <Button
                  onClick={() => {
                    resetForm();
                    setIsModalOpen(true);
                  }}
                  className="whitespace-nowrap text-xs sm:text-sm px-3 sm:px-4 py-3 sm:py-2 touch-manipulation"
                >
                  <i className="ri-add-line mr-1 sm:mr-2"></i>
                  Referenz hinzufügen
                </Button>
                <Button
                  onClick={() => {
                    resetTableForm();
                    setIsTableModalOpen(true);
                  }}
                  variant="secondary"
                  className="whitespace-nowrap text-xs sm:text-sm px-3 sm:px-4 py-3 sm:py-2 touch-manipulation"
                >
                  <i className="ri-table-line mr-1 sm:mr-2"></i>
                  Referenz Tabelle
                </Button>
              </div>
            </div>
          </div>

          <div className="p-3 sm:p-4 lg:p-6 w-full overflow-x-hidden">
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
                <div className="flex">
                  <i className="ri-error-warning-line w-5 h-5 flex items-center justify-center text-red-400 mr-2"></i>
                  <div>
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                  <button
                    onClick={() => setError('')}
                    className="ml-auto pl-3 touch-manipulation"
                  >
                    <i className="ri-close-line w-4 h-4 flex items-center justify-center text-red-400 hover:text-red-600"></i>
                  </button>
                </div>
              </div>
            )}

            {/* Search and filter UI */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
              <Input
                placeholder="Referenzen durchsuchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full text-xs sm:text-sm"
              />
              <Select
                options={[
                  { value: '', label: 'Alle Kategorien' },
                  ...categories.map(cat => ({ value: cat, label: cat }))
                ]}
                value={selectedCategory}
                onChange={(value) => setSelectedCategory(value)}
                placeholder="Kategorie filtern"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div className="bg-blue-50 p-3 sm:p-4 rounded-lg border">
                <div className="flex items-center">
                  <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg mr-2 sm:mr-3">
                    <i className="ri-bookmark-line text-blue-600 w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <div className="text-lg sm:text-2xl font-bold text-blue-600">{references.length + referenceTables.length}</div>
                    <div className="text-xs sm:text-sm text-gray-600">Referenzen gesamt</div>
                  </div>
                </div>
              </div>
              <div className="bg-green-50 p-3 sm:p-4 rounded-lg border">
                <div className="flex items-center">
                  <div className="p-1.5 sm:p-2 bg-green-100 rounded-lg mr-2 sm:mr-3">
                    <i className="ri-folder-line text-green-600 w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <div className="text-lg sm:text-2xl font-bold text-green-600">{Object.keys(groupedItems).length}</div>
                    <div className="text-xs sm:text-sm text-gray-600">Kategorien</div>
                  </div>
                </div>
              </div>
              <div className="bg-orange-50 p-3 sm:p-4 rounded-lg border">
                <div className="flex items-center">
                  <div className="p-1.5 sm:p-2 bg-orange-100 rounded-lg mr-2 sm:mr-3">
                    <i className="ri-search-line text-orange-600 w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <div className="text-lg sm:text-2xl font-bold text-orange-600">{allFilteredItems.length}</div>
                    <div className="text-xs sm:text-sm text-gray-600">Gefiltert</div>
                  </div>
                </div>
              </div>
            </div>

            {Object.keys(groupedItems).length === 0 ? (
              <div className="text-center py-8 sm:py-12">
                <div className="text-gray-400 mb-3 sm:mb-4">
                  <i className="ri-bookmark-line text-4xl sm:text-6xl w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mx-auto"></i>
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-2">
                  {references.length + referenceTables.length === 0 ? 'Keine Referenzen vorhanden' : 'Keine Ergebnisse'}
                </h3>
                <p className="text-gray-500 mb-3 sm:mb-4 text-sm sm:text-base">
                  {references.length + referenceTables.length === 0 
                    ? 'Erstellen Sie Ihre erste Preis-Referenz.'
                    : 'Versuchen Sie andere Suchbegriffe oder Filter.'
                  }
                </p>
                {references.length + referenceTables.length === 0 && (
                  <div className="flex flex-col sm:flex-row gap-2 justify-center">
                    <Button onClick={() => {
                      resetForm();
                      setIsModalOpen(true);
                    }} className="text-xs sm:text-sm py-3 sm:py-2 touch-manipulation">
                      <i className="ri-add-line mr-2"></i>
                      Erste Referenz erstellen
                    </Button>
                    <Button onClick={() => {
                      resetTableForm();
                      setIsTableModalOpen(true);
                    }} variant="secondary" className="text-xs sm:text-sm py-3 sm:py-2 touch-manipulation">
                      <i className="ri-table-line mr-2"></i>
                      Erste Tabelle erstellen
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4 sm:space-y-8">
                {Object.entries(groupedItems).map(([category, items]) => (
                  <div key={category} className="bg-white border rounded-lg shadow-sm">
                    <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gray-50">
                      <h3 className="text-sm sm:text-lg font-semibold text-gray-900 flex items-center">
                        <i className="ri-folder-line mr-2 text-blue-600 text-sm sm:text-base"></i>
                        {category}
                        <span className="ml-2 px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs bg-blue-100 text-blue-600 rounded-full">
                          {items.length}
                        </span>
                      </h3>
                    </div>
                    <div className="p-3 sm:p-6">
                      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
                        {items.map((item) => {
                          const isTable = 'items' in item;
                          if (isTable) {
                            const table = item as ReferenceTable;
                            return (
                              <div key={table.id} className="bg-gray-50 p-3 sm:p-4 rounded-lg border hover:shadow-md transition-shadow">
                                <div className="flex items-start justify-between mb-2 sm:mb-3">
                                  <div className="flex-1 min-w-0">
                                    <h4 className="font-medium text-gray-900 break-words text-sm sm:text-base flex items-center">
                                      <i className="ri-table-line mr-1 text-purple-600"></i>
                                      {table.name}
                                    </h4>
                                    <p className="text-xs sm:text-sm text-gray-500">
                                      {table.items.length} Produkte
                                    </p>
                                  </div>
                                  <div className="flex flex-col sm:flex-row space-y-1 sm:space-y-0 sm:space-x-1 ml-2">
                                    <button
                                      onClick={() => handleTableView(table)}
                                      className="p-1.5 text-gray-400 hover:text-green-600 cursor-pointer rounded-md hover:bg-green-50 touch-manipulation"
                                      title="Tabelle anzeigen"
                                    >
                                      <i className="ri-eye-line w-4 h-5 sm:w-5 sm:h-5 flex items-center justify-center"></i>
                                    </button>
                                    <button
                                      onClick={() => handleTableEdit(table)}
                                      className="p-1.5 text-gray-400 hover:text-blue-600 cursor-pointer rounded-md hover:bg-blue-50 touch-manipulation"
                                      title="Bearbeiten"
                                    >
                                      <i className="ri-edit-line w-4 h-5 sm:w-5 sm:h-5 flex items-center justify-center"></i>
                                    </button>
                                    <button
                                      onClick={() => handleTableDelete(table.id)}
                                      className="p-1.5 text-gray-400 hover:text-red-600 cursor-pointer rounded-md hover:bg-red-50 touch-manipulation"
                                      title="Löschen"
                                    >
                                      <i className="ri-delete-bin-line w-4 h-5 sm:w-5 sm:h-5 flex items-center justify-center"></i>
                                    </button>
                                  </div>
                                </div>

                                <div className="flex items-center justify-between mb-2 sm:mb-3">
                                  <div className="text-lg sm:text-xl font-semibold text-purple-600">
                                    {table.totalPrice.toFixed(2)} CHF
                                  </div>
                                  <div className="text-sm text-gray-600 bg-white px-2 py-1 rounded border">
                                    Gesamt
                                  </div>
                                </div>

                                {table.notes && (
                                  <div className="mb-2 sm:mb-3 pt-2 border-t border-gray-200">
                                    <p className="text-gray-600 text-xs sm:text-sm break-words">{table.notes}</p>
                                  </div>
                                )}

                                <div className="text-xs text-gray-400">
                                  Aktualisiert: {new Date(table.lastUpdated).toLocaleDateString('de-DE')}
                                </div>
                              </div>
                            );
                          } else {
                            const ref = item as Reference;
                            return (
                              <div key={ref.id} className="bg-gray-50 p-3 sm:p-4 rounded-lg border hover:shadow-md transition-shadow">
                                <div className="flex items-start justify-between mb-2 sm:mb-3">
                                  <div className="flex-1 min-w-0">
                                    <h4 className="font-medium text-gray-900 break-words text-sm sm:text-base">{ref.name}</h4>
                                    {ref.supplier && (
                                      <p className="text-xs sm:text-sm text-gray-500 break-words">
                                        <i className="ri-store-line mr-1"></i>
                                        {ref.supplier}
                                      </p>
                                    )}
                                  </div>
                                  <div className="flex flex-col sm:flex-row space-y-1 sm:space-y-0 sm:space-x-1 ml-2">
                                    {ref.file_name && ref.file_url && (
                                      <button
                                        onClick={() => handleFileView(ref.file_url!, ref.file_name!)}
                                        className="p-1.5 text-gray-400 hover:text-green-600 cursor-pointer rounded-md hover:bg-green-50 touch-manipulation"
                                        title="Datei herunterladen"
                                      >
                                        <i className="ri-download-line w-4 h-5 sm:w-5 sm:h-5 flex items-center justify-center"></i>
                                      </button>
                                    )}
                                    <button
                                      onClick={() => handleEdit(ref)}
                                      className="p-1.5 text-gray-400 hover:text-blue-600 cursor-pointer rounded-md hover:bg-blue-50 touch-manipulation"
                                      title="Bearbeiten"
                                    >
                                      <i className="ri-edit-line w-4 h-5 sm:w-5 sm:h-5 flex items-center justify-center"></i>
                                    </button>
                                    <button
                                      onClick={() => handleDelete(ref.id)}
                                      className="p-1.5 text-gray-400 hover:text-red-600 cursor-pointer rounded-md hover:bg-red-50 touch-manipulation"
                                      title="Löschen"
                                    >
                                      <i className="ri-delete-bin-line w-4 h-5 sm:w-5 sm:h-5 flex items-center justify-center"></i>
                                    </button>
                                  </div>
                                </div>

                                <div className="flex items-center justify-between mb-2 sm:mb-3">
                                  <div className="text-lg sm:text-xl font-semibold text-blue-600">
                                    {ref.price.toFixed(2)}
                                  </div>
                                  <div className="text-sm text-gray-600 bg-white px-2 py-1 rounded border">
                                    {ref.unit}
                                  </div>
                                </div>

                                {ref.file_name && (
                                  <div className="mb-2 sm:mb-3 pt-2 border-t border-gray-200">
                                    <div className="flex items-center text-xs sm:text-sm text-blue-600">
                                      <i className="ri-attachment-line mr-1"></i>
                                      <span className="break-words flex-1">{ref.file_name}</span>
                                      <button
                                        onClick={() => handleFileView(ref.file_url!, ref.file_name!)}
                                        className="ml-2 text-blue-500 hover:text-blue-700 touch-manipulation"
                                        title="Herunterladen"
                                      >
                                        <i className="ri-download-line"></i>
                                      </button>
                                    </div>
                                  </div>
                                )}

                                {ref.notes && (
                                  <div className="mb-2 sm:mb-3 pt-2 border-t border-gray-200">
                                    <p className="text-gray-600 text-xs sm:text-sm break-words">{ref.notes}</p>
                                  </div>
                                )}

                                <div className="text-xs text-gray-400">
                                  Aktualisiert: {new Date(ref.lastUpdated).toLocaleDateString('de-DE')}
                                </div>
                              </div>
                            );
                          }
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Existing Reference Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          resetForm();
        }}
        title={editingReference ? 'Referenz bearbeiten' : 'Neue Referenz hinzufügen'}
        maxWidth="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <div className="flex">
                <i className="ri-error-warning-line w-5 h-5 flex items-center justify-center text-red-400 mr-2"></i>
                <div>
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Kategorie"
              options={categories.map(cat => ({ value: cat, label: cat }))}
              value={form.category}
              onChange={(value) => setForm(prev => ({ ...prev, category: value }))}
              required
            />

            <Input
              label="Name/Bezeichnung"
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="z.B. Getränkeautomat XL"
              required
            />

            <Input
              label="Preis"
              type="number"
              step="0.01"
              min="0"
              value={form.price}
              onChange={(e) => setForm(prev => ({ ...prev, price: e.target.value }))}
              placeholder="0.00"
              required
            />

            <Select
              label="Einheit"
              options={units.map(unit => ({ value: unit, label: unit }))}
              value={form.unit}
              onChange={(value) => setForm(prev => ({ ...prev, unit: value }))}
              required
            />

            <Input
              label="Lieferant/Quelle (optional)"
              value={form.supplier}
              onChange={(e) => setForm(prev => ({ ...prev, supplier: e.target.value }))}
              placeholder="z.B. AutomatTech AG"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Datei hochladen (optional)
            </label>
            <input
              type="file"
              onChange={handleFileChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls"
            />
            <p className="mt-1 text-xs text-gray-500">
              Erlaubte Dateiformate: PDF, DOC, DOCX, JPG, PNG, XLSX, XLS
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notizen (optional)
            </label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
              rows={3}
              placeholder="Zusätzliche Informationen, Links, etc."
              maxLength={500}
            />
          </div>

          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 pt-3 sm:pt-4">
            <Button
              type="submit"
              className="flex-1 text-xs sm:text-sm py-3 sm:py-2 touch-manipulation"
              disabled={saving || uploading}
            >
              {uploading ? (
                <>
                  <i className="ri-loader-line w-4 h-4 flex items-center justify-center animate-spin mr-2"></i>
                  Wird hochgeladen...
                </>
              ) : saving ? (
                <>
                  <i className="ri-loader-line w-4 h-4 flex items-center justify-center animate-spin mr-2"></i>
                  Wird gespeichert...
                </>
              ) : (
                <>
                  <i className="ri-save-line mr-2"></i>
                  {editingReference ? 'Änderungen speichern' : 'Referenz hinzufügen'}
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setIsModalOpen(false);
                resetForm();
              }}
              className="flex-1 text-xs sm:text-sm py-3 sm:py-2 touch-manipulation"
            >
              Abbrechen
            </Button>
          </div>
        </form>
      </Modal>

      {/* Reference Table Modal */}
      <Modal
        isOpen={isTableModalOpen}
        onClose={() => {
          setIsTableModalOpen(false);
          resetTableForm();
        }}
        title={editingTable ? 'Referenz-Tabelle bearbeiten' : 'Neue Referenz-Tabelle hinzufügen'}
        maxWidth="xl"
      >
        <form onSubmit={handleTableSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <div className="flex">
                <i className="ri-error-warning-line w-5 h-5 flex items-center justify-center text-red-400 mr-2"></i>
                <div>
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Tabellen-Name"
              value={tableForm.name}
              onChange={(e) => setTableForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="z.B. Getränkeautomat Kosten"
              required
            />

            <Select
              label="Kategorie"
              options={categories.map(cat => ({ value: cat, label: cat }))}
              value={tableForm.category}
              onChange={(value) => setTableForm(prev => ({ ...prev, category: value }))}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Produkte und Preise
            </label>
            <div className="space-y-2">
              {tableForm.items.map((item, index) => (
                <div key={index} className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                  <Input
                    placeholder="Produkt"
                    value={item.product}
                    onChange={(e) => updateTableItem(index, 'product', e.target.value)}
                    className="flex-1"
                  />
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="Preis"
                      value={item.price || ''}
                      onChange={(e) => updateTableItem(index, 'price', parseFloat(e.target.value) || 0)}
                      className="w-24"
                    />
                    <span className="text-sm text-gray-500 w-8">CHF</span>
                    {tableForm.items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeTableItem(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-md touch-manipulation"
                        title="Entfernen"
                      >
                        <i className="ri-delete-bin-line w-4 h-4 flex items-center justify-center"></i>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addTableItem}
              className="mt-2 text-sm text-blue-600 hover:text-blue-800 flex items-center touch-manipulation"
            >
              <i className="ri-add-line mr-1"></i>
              Produkt hinzufügen
            </button>
          </div>

          <div className="bg-blue-50 p-3 rounded-lg border">
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-700">Gesamtpreis:</span>
              <span className="text-xl font-bold text-blue-600">{calculateTotalPrice().toFixed(2)} CHF</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notizen (optional)
            </label>
            <textarea
              value={tableForm.notes}
              onChange={(e) => setTableForm(prev => ({ ...prev, notes: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
              rows={3}
              placeholder="Zusätzliche Informationen..."
              maxLength={500}
            />
          </div>

          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 pt-4">
            <Button
              type="submit"
              className="flex-1 text-sm py-3 sm:py-2 touch-manipulation"
              disabled={saving}
            >
              {saving ? (
                <>
                  <i className="ri-loader-line w-4 h-4 flex items-center justify-center animate-spin mr-2"></i>
                  Wird gespeichert...
                </>
              ) : (
                <>
                  <i className="ri-save-line mr-2"></i>
                  {editingTable ? 'Änderungen speichern' : 'Tabelle hinzufügen'}
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setIsTableModalOpen(false);
                resetTableForm();
              }}
              className="flex-1 text-sm py-3 sm:py-2 touch-manipulation"
            >
              Abbrechen
            </Button>
          </div>
        </form>
      </Modal>

      {/* Table View Modal */}
      <Modal
        isOpen={isTableViewModalOpen}
        onClose={() => {
          setIsTableViewModalOpen(false);
          setViewingTable(null);
        }}
        title={viewingTable?.name || 'Referenz-Tabelle'}
        maxWidth="lg"
      >
        {viewingTable && (
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-2 break-words">{viewingTable.name}</h3>
              <p className="text-sm text-gray-600">Kategorie: {viewingTable.category}</p>
              {viewingTable.notes && (
                <p className="text-sm text-gray-600 mt-1 break-words">Notizen: {viewingTable.notes}</p>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-900">Produkt</th>
                    <th className="border border-gray-300 px-4 py-2 text-right font-medium text-gray-900">Preis (CHF)</th>
                  </tr>
                </thead>
                <tbody>
                  {viewingTable.items.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="border border-gray-300 px-4 py-2 break-words">{item.product}</td>
                      <td className="border border-gray-300 px-4 py-2 text-right">{item.price.toFixed(2)}</td>
                    </tr>
                  ))}
                  <tr className="bg-blue-50 font-semibold">
                    <td className="border border-gray-300 px-4 py-2">Gesamtpreis</td>
                    <td className="border border-gray-300 px-4 py-2 text-right text-blue-600">
                      {viewingTable.totalPrice.toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="text-xs text-gray-400">
              Zuletzt aktualisiert: {new Date(viewingTable.lastUpdated).toLocaleDateString('de-DE')}
            </div>

            <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-2">
              <Button
                onClick={() => {
                  setIsTableViewModalOpen(false);
                  handleTableEdit(viewingTable);
                }}
                variant="secondary"
                className="text-sm py-3 sm:py-2 touch-manipulation"
              >
                <i className="ri-edit-line mr-2"></i>
                Bearbeiten
              </Button>
              <Button
                onClick={() => {
                  setIsTableViewModalOpen(false);
                  setViewingTable(null);
                }}
                className="text-sm py-3 sm:py-2 touch-manipulation"
              >
                Schließen
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
